import * as React from 'react';

// Hooks
import useCountdown from '@shopee/countdown-hook';
import { useStateMachine } from '@shopee/state-machine';

// Utils
import { getEnv } from '@shopee/global-env';
import { getInitialFlow } from './utils';

// Constants & enums
import { stateMachine } from './stateMachines';
import {
  TransitionEvent,
  InitialFlow,
  DEFAULT_OTP_PARAMS,
  DEFAULT_OTP_CONFIG_PARTIAL,
  DEFAULT_CAPTCHA_CONFIG,
} from './constants';
import {
  Feature as StaticFeature,
  isFeatureEnabled as isStaticFeatureEnabled,
} from '@shopee-user/static-feature-toggle';

import {
  OTP_COUNTDOWN_TIME,
  OTP_COUNTDOWN_TIME_IN_TEST_ENVIRONMENT,
  OtpType,
  OtpTransitionState,
  DialogType,
  AuthKeyType,
  SendOtpResult,
  UseCase,
} from '@shopee-user/otp-hook-base-constants';
import PROGRESS, { ApiProgress, API_PROGRESS_INIT } from '@shopee/api-progress';
import { ErrorCode } from '@shopee-user/api-constants';

// Types
import {
  OtpParams,
  OtpConfig,
  CaptchaConfig,
  UseOtpStateBaseProps,
} from '@shopee-user/otp-hook-base-types';

export default function useOtpStateBase<R, S, T, U>({
  operation,
  supportedChannels = [],
  supportDynamicCaptcha = false,
  shouldResetOtpFlowUponSuccessfulVerification = true,
  api: { apiGetOtpSettings, apiSendOtp, apiVerifyOtp },
  currentUseCase,
  actions: { switchToAnotherUsecase },
}: UseOtpStateBaseProps<R, S, T, U>) {
  const DEFAULT_OTP_CONFIG: OtpConfig<R> = {
    ...DEFAULT_OTP_CONFIG_PARTIAL,
    operation,
  };
  const { state, transition } = useStateMachine<
    OtpTransitionState,
    TransitionEvent
  >(stateMachine, OtpTransitionState.Init);
  const [otpParams, setOtpParams] = React.useState<OtpParams>(
    DEFAULT_OTP_PARAMS
  );
  const [otpConfig, setOtpConfig] = React.useState<OtpConfig<R>>(
    DEFAULT_OTP_CONFIG
  );
  const [otpToken, setOtpToken] = React.useState<string>('');
  const [displayedAuthKey, setDisplayedAuthKey] = React.useState<string>('');
  const [captchaConfig, setCaptchaConfig] = React.useState<CaptchaConfig>(
    DEFAULT_CAPTCHA_CONFIG
  );
  const [dialogState, setDialogState] = React.useState<DialogType>(
    DialogType.None
  );
  const [apiState, setApiState] = React.useState<ApiProgress>(
    API_PROGRESS_INIT
  );

  const isWhatsappAuthLinkSupported =
    isStaticFeatureEnabled(StaticFeature.SupportWAL, false) &&
    supportedChannels.includes(OtpType.WhatsappAuthLink);

  const defaultCooldown =
    getEnv() === 'test'
      ? OTP_COUNTDOWN_TIME_IN_TEST_ENVIRONMENT
      : OTP_COUNTDOWN_TIME;

  const {
    startCountdown: startResendCountdown,
    count: resendCountdown,
    active: isResendCountdownActive,
  } = useCountdown({ countdownTime: 0 });

  // We will reset the OTP flow once OTP flow is successful
  React.useEffect(() => {
    if (
      shouldResetOtpFlowUponSuccessfulVerification &&
      state === OtpTransitionState.Success
    ) {
      reset();
    }
  }, [state]);

  const _sendOtpToChannel = (type: OtpType) => {
    return _sendOtp({
      type,
      encryptedAuthKey: otpParams.encryptedAuthKey,
      authKey: otpParams.authKey,
      authKeyType: otpParams.authKeyType,
      token: otpParams.token,
      captchaSignature: captchaConfig.captchaSignature,
    });
  };

  const _sendOtpToSpecificChannel = (type: OtpType) => async () => {
    return _sendOtpToChannel(type);
  };

  /* eslint-disable max-statements, complexity */
  const _sendOtp = async ({
    type,
    encryptedAuthKey,
    authKey,
    authKeyType,
    token,
    captchaSignature,
  }: {
    type?: OtpType;
    encryptedAuthKey?: string;
    authKey: string;
    authKeyType?: AuthKeyType;
    token?: string;
    captchaSignature?: string;
  }) => {
    setApiState({
      apiProgress: PROGRESS.REQ,
      error: null,
      error_msg: null,
    });

    const {
      result,
      latestSentChannel,
      availableChannels,
      hiddenChannels,
      remainingResendCountdown,
      seed,
      data,
      error,
      error_msg,
    } = await apiSendOtp({
      encryptedAuthKey,
      authKey,
      authKeyType,
      token,
      captchaSignature,
      operation,
      supportedChannels,
      channel: type,
      state: {
        otpState: state,
        dialogState,
      },
    });

    setDialogState(DialogType.None);
    setOtpConfig(config => {
      return {
        ...config,
        type,
        availableChannels: availableChannels || otpConfig.availableChannels,
        hiddenChannels: hiddenChannels || otpConfig.hiddenChannels,
        latestSentChannel,
        remainingResendCountdown,
        seed,
      };
    });

    if (result === SendOtpResult.Error) {
      setApiState({
        apiProgress: PROGRESS.ERR,
        error,
        error_msg,
      });
      return { data, error, error_msg };
    }

    switch (result) {
      case SendOtpResult.Success:
        if (remainingResendCountdown !== 0) {
          startResendCountdown(remainingResendCountdown || defaultCooldown);
        }
        break;
      case SendOtpResult.NeedCaptcha:
        if (supportDynamicCaptcha) {
          setDialogState(DialogType.Captcha);
        }
        break;
      case SendOtpResult.NeedVoice:
        setDialogState(DialogType.VoiceOtpConfirmation);
        break;
      case SendOtpResult.OtpServiceUnavailable:
        transition(TransitionEvent.ProceedToServiceUnavailable)();
        break;
      case SendOtpResult.OtpChannelUnavailable:
        setDialogState(DialogType.SwitchChannelConfirmation);
        break;
      default:
        break;
    }

    setApiState({
      apiProgress: PROGRESS.OK,
      error: null,
      error_msg: null,
    });
    return {
      data,
    };
  };

  const verifyOtp = async (otp: string) => {
    setApiState({
      apiProgress: PROGRESS.REQ,
      error: null,
      error_msg: null,
    });
    const { otpToken, data, error, error_msg } = await apiVerifyOtp({
      otp,
      encryptedAuthKey: otpParams.encryptedAuthKey,
      authKey: otpParams.authKey,
      authKeyType: otpParams.authKeyType,
      token: otpParams.token,
      operation: otpConfig.operation,
    });

    if (!error && otpToken) {
      setOtpToken(otpToken);
      setApiState({
        apiProgress: PROGRESS.OK,
        error: null,
        error_msg: null,
      });
      return {
        otpToken,
        data,
      };
    }
    setApiState({
      apiProgress: PROGRESS.ERR,
      error,
      error_msg,
    });
    return { otpToken, data, error, error_msg };
  };

  const showInitialDialog = async ({
    preferredChannel,
  }: {
    preferredChannel: OtpType;
  }) => {
    switch (preferredChannel) {
      case OtpType.WhatsApp:
        setDialogState(DialogType.WhatsAppOtpConfirmation);
        break;
      case OtpType.Voice:
        setDialogState(DialogType.VoiceOtpConfirmation);
        break;
      case OtpType.Zalo:
        setDialogState(DialogType.ZaloOtpConfirmation);
        break;
      case OtpType.Viber:
        setDialogState(DialogType.ViberOtpConfirmation);
        break;
      case OtpType.Sms:
      default:
        break;
    }
  };

  /**
   * Common use cases:
   * 1. BE requires phone or email parameter
   * - beginOtpFlow({ authKey: '1234567890', authKeyType: AuthKeyType.Phone })
   * - beginOtpFlow({ authKey: '123@123.com', authKeyType: AuthKeyType.Email })
   *
   * 2. BE requires encrypted phone or encrypted email parameter
   * - beginOtpFlow({ encryptedAuthKey: 'm_token string', authKeyType: AuthKeyType.Phone })
   * - beginOtpFlow({ encryptedAuthKey: 'encrypted_email string', authKeyType: AuthKeyType.Email })
   *
   * 3. BE requires avToken (encrypted userid) - Only Phone OTP verification
   * - beginOtpFlow({ token: 'avToken', authKeyType: AuthKeyType.Phone })
   *
   * 4. BE doesn't require any auth key (user already logged-in)
   * (Please be aware that this case is only for special operation codes)
   * - beginOtpFlow({})
   *
   */
  const beginOtpFlow = async ({
    maskedAuthKey = '',
    encryptedAuthKey = '',
    authKey = '',
    authKeyType,
    token,
    supportedChannels: supportedChannelsInternal,
    captchaSignature,
  }: {
    maskedAuthKey?: string;
    encryptedAuthKey?: string;
    authKey?: string;
    authKeyType?: AuthKeyType;
    token?: string;
    supportedChannels?: OtpType[];
    captchaSignature?: string;
  }): Promise<{ error?: number; error_msg?: string; data?: S }> => {
    setOtpParams({
      encryptedAuthKey,
      authKey,
      authKeyType,
      token,
    });

    setApiState({
      apiProgress: PROGRESS.REQ,
      error: null,
      error_msg: null,
    });
    // Passing either phone or mToken is ok.
    // If both are passed in, we will not send `phone` param to server, since mToken have precedence.
    const {
      displayedAuthKey,
      preferredChannel,
      availableChannels,
      hiddenChannels,
      latestSentChannel,
      remainingResendCountdown,
      needCaptcha,
      captchaAppKey,
      captchaScene,
      serviceNotAvailable,
      data,
      error,
      error_msg,
    } = await apiGetOtpSettings({
      operation: otpConfig.operation,
      maskedAuthKey,
      encryptedAuthKey,
      authKey,
      authKeyType,
      token,
      supportedChannels: supportedChannelsInternal || supportedChannels,
      captchaSignature,
    });

    // Generic unhandled error
    if (error && !serviceNotAvailable) {
      setApiState({
        apiProgress: PROGRESS.ERR,
        error,
        error_msg,
      });
      return { data, error, error_msg };
    }

    // Successful case or specially handled error
    setApiState({
      apiProgress: PROGRESS.OK,
      error: null,
      error_msg: null,
    });
    if (serviceNotAvailable) {
      transition(TransitionEvent.ProceedToServiceUnavailable)();
    } else {
      setOtpConfig({
        ...otpConfig,
        type: latestSentChannel || preferredChannel,
        availableChannels: availableChannels || otpConfig.availableChannels,
        hiddenChannels: hiddenChannels || otpConfig.hiddenChannels,
        preferredChannel,
        remainingResendCountdown,
        latestSentChannel,
      });
      setCaptchaConfig({
        captchaAppKey,
        captchaScene,
        captchaSignature: captchaSignature || '',
      });
      setDisplayedAuthKey(displayedAuthKey || authKey);

      const { initialFlow, initialUsecase } = getInitialFlow({
        needCaptcha,
        preferredChannel,
        latestSentChannel,
        currentUseCase,
      });

      /**
       * To remove when StaticFeature.SupportWAL is greyscaled to 100%
       */
      if (
        !isWhatsappAuthLinkSupported &&
        (initialUsecase === UseCase.WhatsappAuthLink ||
          preferredChannel === OtpType.WhatsappAuthLink)
      ) {
        setApiState({
          apiProgress: PROGRESS.ERR,
          error: ErrorCode.FeErrorCode,
          error_msg: 'Verification Method not supported',
        });
        return { data, error, error_msg };
      }

      switch (initialFlow) {
        case InitialFlow.ShowCaptcha:
          setDialogState(DialogType.Captcha);
          break;
        case InitialFlow.ShowConfirmationDialog:
          showInitialDialog({ preferredChannel });
          break;
        case InitialFlow.SelectOtpChannelInitial:
          transition(TransitionEvent.ProceedToSelectOtpChannelInitial)();
          break;
        case InitialFlow.SkipSendOtp:
          if (remainingResendCountdown !== 0) {
            startResendCountdown(remainingResendCountdown || defaultCooldown);
          }
          transition(TransitionEvent.ProceedToRequireOtp)();
          break;
        case InitialFlow.SwitchUsecase: {
          const { error, error_msg } = switchToAnotherUsecase({
            usecase: initialUsecase,
            authKey,
            authKeyType,
            captchaRequired: false,
            captchaSignature,
            captchaAppKey,
            captchaScene,
            sessionInfo: {
              remaining_resend_cooldown_sec: remainingResendCountdown,
              latest_sent_channel: latestSentChannel,
            },
          });
          if (!error) {
            // Switch flow is successful, reset the OTP flow
            reset();
          }
          return { data, error, error_msg };
        }
        default:
          await _sendOtp({
            type: latestSentChannel || preferredChannel,
            encryptedAuthKey,
            authKey,
            authKeyType,
            token,
            captchaSignature,
          });
          transition(TransitionEvent.ProceedToRequireOtp)();
      }
    }
    return { data };
  };

  const onVerifyCaptcha = async ({
    captchaSignature,
  }: {
    captchaSignature: string;
  }) => {
    setCaptchaConfig(config => {
      return {
        ...config,
        captchaSignature,
      };
    });

    switch (state) {
      case OtpTransitionState.Init: {
        const { initialFlow, initialUsecase } = getInitialFlow({
          needCaptcha: false,
          preferredChannel: otpConfig.preferredChannel,
          latestSentChannel: otpConfig.latestSentChannel,
          currentUseCase,
        });
        /**
         * To remove when StaticFeature.SupportWAL is greyscaled to 100%
         */
        if (
          !isWhatsappAuthLinkSupported &&
          (initialUsecase === UseCase.WhatsappAuthLink ||
            otpConfig.preferredChannel === OtpType.WhatsappAuthLink)
        ) {
          setApiState({
            apiProgress: PROGRESS.ERR,
            error: ErrorCode.FeErrorCode,
            error_msg: 'Verification Method not supported',
          });
          return;
        }

        switch (initialFlow) {
          case InitialFlow.ShowConfirmationDialog:
            showInitialDialog({ preferredChannel: otpConfig.preferredChannel });
            break;
          case InitialFlow.SelectOtpChannelInitial:
            setDialogState(DialogType.None);
            transition(TransitionEvent.ProceedToSelectOtpChannelInitial)();
            break;
          case InitialFlow.SkipSendOtp:
            setDialogState(DialogType.None);
            if (otpConfig.remainingResendCountdown !== 0) {
              startResendCountdown(
                otpConfig.remainingResendCountdown || defaultCooldown
              );
            }
            transition(TransitionEvent.ProceedToRequireOtp)();
            break;
          case InitialFlow.SwitchUsecase: {
            setDialogState(DialogType.None);
            const { error, error_msg } = switchToAnotherUsecase({
              usecase: initialUsecase,
              authKey: otpParams.authKey,
              authKeyType: otpParams.authKeyType,
              captchaRequired: false,
              captchaSignature,
              captchaAppKey: captchaConfig.captchaAppKey,
              captchaScene: captchaConfig.captchaScene,
              sessionInfo: {
                remaining_resend_cooldown_sec:
                  otpConfig.remainingResendCountdown,
                latest_sent_channel: otpConfig.latestSentChannel,
              },
            });
            if (!error) {
              // Switch flow is successful, reset the OTP flow
              reset();
            } else {
              setApiState({
                apiProgress: PROGRESS.ERR,
                error,
                error_msg,
              });
            }
            break;
          }
          default:
            await _sendOtp({
              type: otpConfig.type,
              encryptedAuthKey: otpParams.encryptedAuthKey,
              authKey: otpParams.authKey,
              authKeyType: otpParams.authKeyType,
              token: otpParams.token,
              captchaSignature,
            });
            transition(TransitionEvent.ProceedToRequireOtp)();
        }
        break;
      }
      default: {
        await _sendOtp({
          type: otpConfig.type,
          encryptedAuthKey: otpParams.encryptedAuthKey,
          authKey: otpParams.authKey,
          authKeyType: otpParams.authKeyType,
          token: otpParams.token,
          captchaSignature,
        });
        break;
      }
    }
  };

  const reset = () => {
    setOtpConfig(DEFAULT_OTP_CONFIG);
    setOtpParams(DEFAULT_OTP_PARAMS);
    setCaptchaConfig(DEFAULT_CAPTCHA_CONFIG);
    setApiState(API_PROGRESS_INIT);
    transition(TransitionEvent.Reset)();
  };

  return {
    state,
    otpParams,
    otpConfig,
    captchaConfig,
    otpToken,
    dialogState,
    displayedAuthKey,
    isDialogShown: dialogState !== DialogType.None,
    apiState: {
      ...apiState,
      inProgress: apiState.apiProgress === PROGRESS.REQ,
    },
    countdown: {
      count: resendCountdown,
      active: isResendCountdownActive,
    },
    transitions: {
      backToPrevious: transition(TransitionEvent.BackToPrevious),
      proceedToNext: transition(TransitionEvent.ProceedToNext),
      proceedToEnforceVoiceOtp: transition(
        TransitionEvent.ProceedToEnforceVoiceOtp
      ),
      proceedToSelectOtpChannel: transition(
        TransitionEvent.ProceedToSelectOtpChannel
      ),
      proceedToSelectOtpChannelInitial: () => {
        setDialogState(DialogType.None);
        transition(TransitionEvent.ProceedToSelectOtpChannelInitial)();
      },
      proceedToRequireOtp: transition(TransitionEvent.ProceedToRequireOtp),
      /**
       * Reset action that can be used to reset the stateMachine as well as the local state.
       * Normally the reset action is not required to be called, as the hook will be unmounted
       * and all states will be reset.
       *
       * But for use cases where the hook is not unmounted after the otp flow is finished, i.e.
       * shopeepay login flow, we provide this reset action for manual reset.
       * legacy-packages/user/authentication-shopeepay/authentication-shopeepay-pc/src/index.tsx
       */
      reset,
    },
    actions: {
      beginOtpFlow,
      sendOtp: async () => {
        const { data, error } = await _sendOtp({
          type: otpConfig.type,
          encryptedAuthKey: otpParams.encryptedAuthKey,
          authKey: otpParams.authKey,
          authKeyType: otpParams.authKeyType,
          token: otpParams.token,
          captchaSignature: captchaConfig.captchaSignature,
        });
        return {
          data,
          error,
        };
      },
      verifyOtp,
      sendOtpToChannel: _sendOtpToChannel,
      onVerifyCaptcha,
      sendWhatsAppOtp: _sendOtpToSpecificChannel(OtpType.WhatsApp),
      sendVoiceOtp: _sendOtpToSpecificChannel(OtpType.Voice),
      sendZaloOtp: _sendOtpToSpecificChannel(OtpType.Zalo),
      sendViberOtp: _sendOtpToSpecificChannel(OtpType.Viber),
      closeDialog: () => {
        setDialogState(DialogType.None);
      },
    },
  };
}
